import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { DayRangeField } from "../components/DayRangeField";
import type { DayAxis } from "../models/day-axis";
import { UploadMultiImages } from "@/components/ui/upload-multi-images";
import { UploadMultiFiles } from "@/components/ui/upload-multi-files";
import { Link, MapPin, Euro, ArrowRight } from "lucide-react";
import { AccommodationActivity } from "../models/activity.model";
import { useI18n } from "@/hooks/useI18n";
import ActivityModalShell from "./ActivityModalShell";
import ActivityDetailView from "./ActivityDetailView";

type AccommodationTab = 'details' | 'media' | 'documents';

const MEDIA_ERROR_FIELDS = ['media'] as const;

interface AccommodationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (activity: Partial<AccommodationActivity>) => void;
    icon?: React.ReactNode;
    initialData?: Partial<AccommodationActivity>;
    // optional itinerary bounds (may be provided by caller)
    itineraryStartDate?: string | null;
    itineraryEndDate?: string | null;
    /** Asse dei giorni: sui template si sceglie "Giorno N" invece di una data. */
    axis?: DayAxis | null;
    itineraryId: string;
    defaultDate?: string;
    /** Nasconde la card data e la rende non obbligatoria (usato dalla Libreria). */
    hideDates?: boolean;
    /** Sola lettura: campi non interagibili, footer con solo "Chiudi" (usato dalla Libreria). */
    readOnly?: boolean;
  /** Il cliente ha gia' pagato: costo e "includi nel prezzo" sono consolidati. */
  pricingLocked?: boolean;
}

const AccommodationModal = ({ isOpen, onClose, onSave, icon, initialData, itineraryStartDate, itineraryEndDate, axis, itineraryId, defaultDate, hideDates = false, readOnly = false, pricingLocked = false }: AccommodationModalProps) => {
    const { t } = useI18n();

    // Generate temporary UUID for new activities
    const [activityId] = useState<string>(() =>
        initialData?.id || crypto.randomUUID()
    );

    const [formData, setFormData] = useState<Partial<AccommodationActivity>>({
        id: activityId,
        include_price: true,
        title: '',
        link: '',
        address: '',
        start_date: undefined,
        end_date: undefined,
        check_in_time: '',
        check_out_time: '',
        check_in_note: '',
        cost: 0,
        refundable_rate: false,
        price_tag: '',
        description: '',
        media: [],
        documentation: []
    });

    // Update form data when initialData changes
    useEffect(() => {
        if (initialData) {
            setFormData({ ...initialData, id: activityId });
        }
    }, [initialData, activityId]);

    const [errors, setErrors] = useState<Record<string, boolean>>({});
    const [showValidationBanner, setShowValidationBanner] = useState(false);
    const [activeTab, setActiveTab] = useState<AccommodationTab>('details');

    useEffect(() => {
        if (!isOpen) {
            setShowValidationBanner(false);
            setErrors({});
            setActiveTab('details');
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && !initialData && defaultDate) {
            setFormData(prev => ({ ...prev, start_date: defaultDate }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const priceTagOptions = [
        'Only accommodation', 'Breakfast Included', 'Half board', 'Full board', 'All inclusive'
    ];

    const validateForm = (): Record<string, boolean> => {
        const requiredFields = hideDates
            ? ['title', 'address', 'check_in_time', 'check_out_time', 'cost', 'price_tag', 'media', 'description']
            : ['title', 'address', 'start_date', 'end_date', 'check_in_time', 'check_out_time', 'cost', 'price_tag', 'media', 'description'];
        const newErrors: Record<string, boolean> = {};

        requiredFields.forEach(field => {
            const value = formData[field as keyof AccommodationActivity];
            if (field === 'cost') {
                newErrors[field] = value === null || value === undefined || (value as number) < 0;
            } else {
                newErrors[field] = !value || (typeof value === 'string' && value.trim() === '');
            }
        });

        setErrors(newErrors);
        return newErrors;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const newErrors = validateForm();
        if (!Object.values(newErrors).some(error => error)) {
            onSave({
                ...formData,
                type: 'accommodation' as const
            });
            onClose();
            return;
        }

        setShowValidationBanner(true);
        if (MEDIA_ERROR_FIELDS.some(field => newErrors[field])) {
            setActiveTab('media');
        } else {
            setActiveTab('details');
        }
    };

    // derive min/max for date picker: prefer explicit props, then common fields on initialData
    const derivedMinDate: string | null =
        itineraryStartDate
        || (initialData as any)?.itineraryStartDate
        || (initialData as any)?.itinerary_start_date
        || (initialData as any)?.min_date
        || (initialData as any)?.minDate
        || null;

    const derivedMaxDate: string | null =
        itineraryEndDate
        || (initialData as any)?.itineraryEndDate
        || (initialData as any)?.itinerary_end_date
        || (initialData as any)?.max_date
        || (initialData as any)?.maxDate
        || null;

    const hasDetailsError = ['title', 'address', 'start_date', 'end_date', 'check_in_time', 'check_out_time', 'cost', 'price_tag', 'description']
        .some(field => errors[field]);
    const hasMediaError = MEDIA_ERROR_FIELDS.some(field => errors[field]);

    if (readOnly) {
        return (
            <ActivityDetailView
                isOpen={isOpen}
                onClose={onClose}
                icon={icon}
                title={t('builder.activities.accommodation')}
                activity={formData}
            />
        );
    }

    return (
        <ActivityModalShell
            isOpen={isOpen}
            onClose={onClose}
            icon={icon}
            title={t('builder.activities.accommodation')}
            formId="accommodation-form"
            onSubmit={handleSubmit}
            showValidationBanner={showValidationBanner}
            activeTab={activeTab}
            onActiveTabChange={(value) => setActiveTab(value as AccommodationTab)}
            tabs={[
                {
                    key: 'details',
                    label: t('modals.tabDetails'),
                    hasError: hasDetailsError,
                    content: (
                        <>
                            {/* Basic Information */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>{t('modals.generalInformation')}</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Input
                                                id="title"
                                                value={formData.title || ''}
                                                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                                placeholder={t('modals.accommodationTitle')}
                                                hasError={errors.title}
                                                readOnly={readOnly}
                                            />
                                        </div>
                                        <div>
                                            <div className="relative">
                                                <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                                                <Input
                                                    id="link"
                                                    type="url"
                                                    value={formData.link || ''}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
                                                    placeholder={t('modals.accommodationLink')}
                                                    className="pl-10"
                                                    hasError={errors.link}
                                                    readOnly={readOnly}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="relative">
                                            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                                            <Input
                                                id="address"
                                                value={formData.address || ''}
                                                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                                                placeholder={t('modals.address')}
                                                className="pl-10"
                                                hasError={errors.address}
                                                readOnly={readOnly}
                                            />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Date Range and Check-in/Check-out Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Date Range Card */}
                                {!hideDates && (
                                    <Card>
                                        <CardHeader>
                                            <CardTitle>{t('modals.from')} - {t('modals.to')}</CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <DayRangeField
                                              axis={axis}
                                              value={formData}
                                              onChange={(patch) => setFormData(prev => ({ ...prev, ...patch }))}
                                              hasError={errors.start_date || errors.end_date}
                                              minDate={derivedMinDate || undefined}
                                              maxDate={derivedMaxDate || undefined}
                                              disabled={readOnly}
                                              // Il check-out cade tipicamente il giorno dopo l'ultima notte.
                                              allowEndBeyondDuration
                                            />
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Check-in & Check-out Card */}
                                <Card className={hideDates ? 'md:col-span-2' : undefined}>
                                    <CardHeader>
                                        <CardTitle>{t('modals.checkInCheckOut')}</CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="flex flex-row justify-evenly items-center">
                                            <div className="flex flex-row items-center">
                                                <Input
                                                    className="w-30"
                                                    type="time"
                                                    value={formData.check_in_time || ''}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, check_in_time: e.target.value }))}
                                                    placeholder={t('modals.checkIn')}
                                                    hasError={errors.check_in_time}
                                                    readOnly={readOnly}
                                                />
                                            </div>
                                            <span>
                                                <ArrowRight className="mx-2 h-4 w-4 text-gray-500" />
                                            </span>
                                            <div className="flex flex-row items-center">
                                                <Input
                                                    className="w-30"
                                                    type="time"
                                                    value={formData.check_out_time || ''}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, check_out_time: e.target.value }))}
                                                    placeholder={t('modals.checkOut')}
                                                    hasError={errors.check_out_time}
                                                    readOnly={readOnly}
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <Textarea
                                                id="checkInNote"
                                                value={formData.check_in_note || ''}
                                                onChange={(e) => setFormData(prev => ({ ...prev, check_in_note: e.target.value }))}
                                                placeholder={t('modals.shareCheckInDetails')}
                                                rows={3}
                                                readOnly={readOnly}
                                            />
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Price Card */}
                            <Card>
                                <CardHeader>
                                    <div className="flex items-center justify-between">
                                        <CardTitle>{t('modals.price')}</CardTitle>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-gray-600">{t('modals.includePrice')}</span>
                                            <Switch
                                                checked={formData.include_price || false}
                                                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, include_price: checked }))}
                                                disabled={readOnly || pricingLocked}
                                            />
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="flex items-center justify-center gap-4">
                                        <div className="flex-1">
                                            <div className="relative">
                                                <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                                                <Input
                                                    id="cost"
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={formData.cost}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, cost: parseFloat(e.target.value) }))}
                                                    placeholder={t('modals.amountOnlyVisibleToYou')}
                                                    className="pl-10"
                                                    hasError={errors.cost}
                                                    readOnly={readOnly || pricingLocked}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <Checkbox
                                                id="refundableRate"
                                                checked={formData.refundable_rate || false}
                                                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, refundable_rate: !!checked }))}
                                                disabled={readOnly}
                                            />
                                            <Label htmlFor="refundableRate">{t('modals.refundableRate')}</Label>
                                        </div>
                                    </div>

                                    {/* Price Tags */}
                                    <div className="mt-4">
                                        <ToggleGroup
                                            type="single"
                                            value={formData.price_tag || ''}
                                            onValueChange={(value) => setFormData(prev => ({ ...prev, price_tag: (value as string) || '' }))}
                                            variant="outline"
                                            className="flex items-start justify-start flex-wrap gap-0 mt-2"
                                            hasError={errors.price_tag}
                                            disabled={readOnly}
                                        >
                                            {priceTagOptions.map((tag, index) => (
                                                <ToggleGroupItem
                                                    key={tag}
                                                    value={tag}
                                                    className={[
                                                        "rounded-none first:ml-0",
                                                        index === 0 ? "rounded-l-lg" : "",
                                                        index === priceTagOptions.length - 1 ? "rounded-r-lg" : "",
                                                    ].join(" ")}
                                                >
                                                    {tag}
                                                </ToggleGroupItem>
                                            ))}
                                        </ToggleGroup>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Description Card */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>{t('modals.description')}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <RichTextEditor
                                        value={formData.description || ''}
                                        onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                                        placeholder={t('modals.describeAccommodation')}
                                        hasError={errors.description}
                                        readOnly={readOnly}
                                    />
                                </CardContent>
                            </Card>
                        </>
                    ),
                },
                {
                    key: 'media',
                    label: t('modals.tabMedia'),
                    hasError: hasMediaError,
                    content: (
                        <Card>
                            <CardHeader>
                                <CardTitle>{t('modals.media')}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <UploadMultiImages
                                    images={formData.media || []}
                                    onImagesChange={(images) => setFormData(prev => ({ ...prev, media: images }))}
                                    label={t('modals.upload4Photos')}
                                    maxImages={10}
                                    hasError={errors.media}
                                    itineraryId={itineraryId}
                                    activityId={activityId}
                                    readOnly={readOnly}
                                />
                            </CardContent>
                        </Card>
                    ),
                },
                {
                    key: 'documents',
                    label: t('modals.tabDocuments'),
                    content: (
                        <Card>
                            <CardHeader>
                                <CardTitle>{t('modals.documentation')}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <UploadMultiFiles
                                    files={formData.documentation || []}
                                    onFilesChange={(files) => setFormData(prev => ({ ...prev, documentation: files }))}
                                    label={t('modals.uploadBookingDocumentation')}
                                    maxFiles={5}
                                    itineraryId={itineraryId}
                                    activityId={activityId}
                                    readOnly={readOnly}
                                />
                            </CardContent>
                        </Card>
                    ),
                },
            ]}
        />
    );
};

export default AccommodationModal;
